import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, ChevronDown as DropIcon, Pencil } from "lucide-react";
import { useInfiniteFeed } from "@/api/useInfiniteFeed";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useProjectFilterOptions } from "../api/projects";
import type { Project } from "../types";
import { ProjectStatusBadge } from "../components/ContractStatusBadge";
import { Button } from "@/components/ui/Button";
import { LoadMoreButton } from "@/components/ui/LoadMoreButton";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";

/** Lista pokazuje status po `is_active` (nie rozróżnia wstrzymania); los umowy
 *  (wypowiedzenie) i tak wypiera tę etykietę wewnątrz ProjectStatusBadge. */
const activeFallback = (p: Project) =>
  p.is_active ? { label: "Aktywne", color: "green" as const } : { label: "Nieaktywne", color: "gray" as const };

type SortField = "short_name" | "name" | "client" | "branch" | "sub_branch" | "coordinator" | "status";
type SortDir = "asc" | "desc";

interface ColFilters {
  client: string[];
  branch: string[];
  sub_branch: string[];
  coordinator: string[];
  status: string[];
}

const EMPTY_FILTERS: ColFilters = {
  client: [],
  branch: [],
  sub_branch: [],
  coordinator: [],
  status: [],
};

// ── MultiSelectFilter ──────────────────────────────────────────────────────────
interface MultiSelectOption { value: string; label: string }

function MultiSelectFilter({
  options,
  selected,
  onChange,
  placeholder = "Wszystkie",
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        dropRef.current && !dropRef.current.contains(e.target as Node)
      ) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleOpen() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: r.width });
    }
    setOpen((v) => !v);
  }

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} wybrane`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`flex items-center gap-1 text-xs border rounded px-2 py-0.5 max-w-[160px] w-full text-left whitespace-nowrap overflow-hidden
          ${selected.length > 0
            ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-red-50 dark:bg-red-900/20"
            : "border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          }`}
      >
        <span className="flex-1 truncate">{label}</span>
        <DropIcon className="w-3 h-3 shrink-0 opacity-50" />
      </button>

      {open && (
        <div
          ref={dropRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, minWidth: Math.max(pos.width, 180), zIndex: 9999 }}
          className="max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg"
        >
          {options.map(({ value, label: optLabel }) => (
            <label
              key={value}
              className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              <input
                type="checkbox"
                checked={selected.includes(value)}
                onChange={() => toggle(value)}
                className="accent-[var(--color-primary)] w-3 h-3 shrink-0"
              />
              <span className="truncate">{optLabel}</span>
            </label>
          ))}
        </div>
      )}
    </>
  );
}

// ── SortIcon ───────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ChevronsUpDown className="inline ml-1 w-3 h-3 opacity-30" />;
  return sortDir === "asc"
    ? <ChevronUp className="inline ml-1 w-3 h-3 text-[var(--color-primary)]" />
    : <ChevronDown className="inline ml-1 w-3 h-3 text-[var(--color-primary)]" />;
}

// ── Page ───────────────────────────────────────────────────────────────────────
export function ProjectsListPage() {
  const { user } = useAuthStore();
  const isCeo = Boolean(user?.is_ceo);
  const isTechnicalDirector = user?.role === "director" && Boolean(user?.is_technical_director);
  const isCoordinator = user?.role === "coordinator";
  // Edit access mirrors the project edit page: CEO / technical director may edit every
  // project; a coordinator may edit the projects they are assigned to (the list endpoint
  // already returns only those for coordinators).
  const canEditProject = (p: Project) =>
    isCeo || isTechnicalDirector ||
    (isCoordinator && !!user && p.coordinators_detail.some((c) => c.id === user.id));
  const showEditColumn = isCeo || isTechnicalDirector || isCoordinator;
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("short_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filters, setFilters] = useState<ColFilters>(EMPTY_FILTERS);
  // Inactive projects are hidden by default; the "Pokaż nieaktywne" checkbox reveals them.
  const [showInactive, setShowInactive] = useState(false);

  // Search now round-trips to the server, so don't fire a request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  const feedFilters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      client_in: filters.client,
      branch_in: filters.branch,
      sub_branch_in: filters.sub_branch,
      coordinator_in: filters.coordinator,
      status_in: filters.status,
      // The backend hides inactive projects unless asked — mirroring what the browser
      // used to do — and an explicit "inactive" in status_in overrides it there too.
      show_inactive: showInactive ? "true" : undefined,
      ordering: `${sortDir === "desc" ? "-" : ""}${sortField}`,
    }),
    [debouncedSearch, filters, showInactive, sortField, sortDir],
  );

  const {
    items: processed,
    isLoading,
    isPlaceholderData,
    total,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    fetchAll,
    isFetchingAll,
  } = useInfiniteFeed<Project>({
    key: ["projects", "feed"],
    url: "/projects/feed/",
    filters: feedFilters,
  });

  // Options used to be derived from the full dataset; with 50 rows loaded they can't be.
  const { data: options } = useProjectFilterOptions();

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  }

  function setFilter(key: keyof ColFilters, value: string[]) {
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function clearAll() {
    setSearch("");
    setFilters(EMPTY_FILTERS);
  }

  const anyFilter = search.trim() || Object.values(filters).some((a) => a.length > 0);
  const opts = useMemo(() => {
    const toChoices = (list?: { id: string; label: string }[]) =>
      (list ?? []).map((o) => ({ value: o.id, label: o.label }));
    return {
      client: toChoices(options?.client),
      branch: toChoices(options?.branch),
      sub_branch: toChoices(options?.sub_branch),
      coordinator: toChoices(options?.coordinator),
      status: toChoices(options?.status),
    };
  }, [options]);

  const thCls = "text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 whitespace-nowrap";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Przedsięwzięcia</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        </div>
        {(user?.role === "director" || user?.has_project_creation_permission) && (
          <Link to="/projects/new">
            <Button size="sm">+ Nowe przedsięwzięcie</Button>
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj skrótu lub nazwy…"
            className="pl-8 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] w-64"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {options?.has_inactive && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-[var(--color-primary)] w-3.5 h-3.5"
            />
            Pokaż nieaktywne
          </label>
        )}
        {anyFilter && (
          <>
            <span className="text-xs text-gray-400">{total} wyników</span>
            <button onClick={clearAll} className="text-xs text-[var(--color-primary)] hover:underline">
              Wyczyść filtry
            </button>
          </>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (
        <div className={isPlaceholderData ? "opacity-60 pointer-events-none" : undefined}>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {processed.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">Brak wyników dla wybranych filtrów.</div>
            ) : processed.map((p) => (
              <div key={p.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Link to={`/projects/${p.id}`} className="font-mono font-semibold text-[var(--color-primary)] hover:underline">{p.short_name}</Link>
                      <ProjectStatusBadge project={p} fallback={activeFallback(p)} />
                    </div>
                    {canEditProject(p) && (
                      <Link
                        to={`/projects/${p.id}/edit`}
                        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] mt-1"
                      >
                        <Pencil size={11} /> Edytuj
                      </Link>
                    )}
                    <Link to={`/projects/${p.id}`} className="text-sm text-gray-800 dark:text-gray-200 hover:underline block mt-0.5">{p.name}</Link>
                    {p.client_detail ? (
                      <Link to={`/clients/${p.client_detail.id}`} className="text-xs text-gray-500 dark:text-gray-400 hover:underline block mt-0.5">{p.client_detail.name}</Link>
                    ) : p.client_name ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{p.client_name}</p>
                    ) : null}
                    {(p.client_branch_detail || p.client_sub_branch_detail) && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {[p.client_branch_detail?.display_name, p.client_sub_branch_detail?.display_name].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {p.coordinators_detail.length > 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {p.coordinators_detail.map((c) => `${c.first_name} ${c.last_name}`).join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800">
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[960px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">Lp.</th>
                <th className={thCls} onClick={() => toggleSort("short_name")}>
                  Skrót <SortIcon field="short_name" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("name")}>
                  Nazwa <SortIcon field="name" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("client")}>
                  Klient <SortIcon field="client" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("branch")}>
                  Oddział <SortIcon field="branch" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("sub_branch")}>
                  Pododdział <SortIcon field="sub_branch" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("coordinator")}>
                  Koordynator <SortIcon field="coordinator" sortField={sortField} sortDir={sortDir} />
                </th>
                <th className={thCls} onClick={() => toggleSort("status")}>
                  Status <SortIcon field="status" sortField={sortField} sortDir={sortDir} />
                </th>
                {showEditColumn && <th className="px-3 py-2" />}
              </tr>
              <tr className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5" />
                <td className="px-3 py-1.5">
                  <MultiSelectFilter options={opts.client} selected={filters.client} onChange={(v) => setFilter("client", v)} placeholder="Wszyscy" />
                </td>
                <td className="px-3 py-1.5">
                  <MultiSelectFilter options={opts.branch} selected={filters.branch} onChange={(v) => setFilter("branch", v)} />
                </td>
                <td className="px-3 py-1.5">
                  <MultiSelectFilter options={opts.sub_branch} selected={filters.sub_branch} onChange={(v) => setFilter("sub_branch", v)} />
                </td>
                <td className="px-3 py-1.5">
                  <MultiSelectFilter options={opts.coordinator} selected={filters.coordinator} onChange={(v) => setFilter("coordinator", v)} placeholder="Wszyscy" />
                </td>
                <td className="px-3 py-1.5">
                  <MultiSelectFilter options={opts.status} selected={filters.status} onChange={(v) => setFilter("status", v)} />
                </td>
                {showEditColumn && <td className="px-3 py-1.5" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {processed.length === 0 ? (
                <tr>
                  <td colSpan={showEditColumn ? 9 : 8} className="px-4 py-6 text-center text-sm text-gray-400">
                    Brak wyników dla wybranych filtrów.
                  </td>
                </tr>
              ) : (
                processed.map((p, idx) => (
                  <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-3 text-gray-400 dark:text-gray-500 whitespace-nowrap">{idx + 1}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <Link to={`/projects/${p.id}`} className="font-mono font-semibold text-[var(--color-primary)] hover:underline">
                        {p.short_name}
                      </Link>
                    </td>
                    <td className="px-3 py-3">
                      <Link to={`/projects/${p.id}`} className="font-medium text-gray-900 dark:text-gray-100 hover:text-[var(--color-primary)] hover:underline">
                        {p.name}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {p.client_detail ? (
                        <Link to={`/clients/${p.client_detail.id}`} className="hover:text-[var(--color-primary)] hover:underline">
                          {p.client_detail.name}
                        </Link>
                      ) : (
                        <span>{p.client_name || <span className="text-gray-300 dark:text-gray-600">—</span>}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {p.client_branch_detail?.display_name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="px-3 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {p.client_sub_branch_detail?.display_name ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {p.coordinators_detail.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {p.coordinators_detail.map((c) => (
                            <span key={c.id} className="text-gray-700 dark:text-gray-300 text-xs">
                              {c.first_name} {c.last_name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <ProjectStatusBadge project={p} fallback={activeFallback(p)} />
                    </td>
                    {showEditColumn && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        {canEditProject(p) && (
                          <Link
                            to={`/projects/${p.id}/edit`}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] transition-colors"
                            title="Edytuj przedsięwzięcie"
                          >
                            <Pencil size={13} />
                          </Link>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          </div>
          {/* One footer for both layouts — they are mutually exclusive by breakpoint. */}
          <LoadMoreButton
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
            onLoadAll={fetchAll}
            isFetchingAll={isFetchingAll}
            loaded={processed.length}
            total={total}
          />
        </div>
      )}
    </div>
  );
}
